/**
 * CREATE.JSX - Purchase Order Creation Component
 *
 * SUMMARY:
 * React component for creating Purchase Orders with proper variation number handling
 * in task selection dropdown. Ensures variation numbers match project summary display.
 *
 * KEY FUNCTIONS:
 * - addVariationNumbersToTasks() - Applies variation numbers to dropdown tasks
 * - projectChangeHandler() - Integrates variation numbering into project data flow
 * - openItemSelectionModal() - Material selection with variation-aware task mapping
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AddMaterialPO from "../../util/add-material-po";
import {
  getMaterialsList,
  sortByKeyAsc,
  getVariationNumber,
  combineData,
} from "../../util/common";
import { toDecimal } from "../../util/decimal-utils";
import Modal from "../../components/modal/modal";
import DatePickerInput from "../../components/inputs/date-picker";
import SelectInput from "../../components/inputs/select";
import CustomInput from "../../components/inputs/new-input";
import { createPOFields } from "./data/create-po";
import TextAreaInput from "../../components/inputs/textarea-input";
import idx from "idx";
import _ from "lodash";
import { deliveryOptions } from "../../constants/fields.constant";
import { ValidateField, ValidatorTypes } from "../../util/validators";
import { useSelector, useDispatch } from "react-redux";
import { convertToReactSelectFormat, filterLockedSections, getTimesheetSectionDropdownOptions } from "../../util/section-dropdown-format";
import MultiSelect from "../inputs/multi-select";
import EditIcon from "@mui/icons-material/Edit";
import {
  SUPPLIERS,
  VENDOR,
  VENDOR_ADD_SUPPLIER,
} from "../../constants/route-paths";
import { getOptionsData } from "src/routes/projects/utils/get-options";
import {
  getTaskdropdownData,
  getStages,
} from "../../actions/project-management.action";
import Button from "@mui/material/Button";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import ItemSelectionModal from "../item-selection-modal";
import "./create.scss";
import TaskMultiSelect from "../inputs/task-multi-select";
import { TaskType } from "../../util/enums";

const CreatePOComponent = ({ projectId, breadcrumb, ...props }) => {
  // For PO creation, the basket icon should only indicate tasks that have materials
  const hasContractorWork = (subcontractorTotalCost, materialTotalCost) =>
    parseFloat(materialTotalCost || 0) > 0;

  const buildContractorWorkLookup = (stages = []) => {
    const contractorWorkLookup = new Map();

    const visitStage = (stage) => {
      if (Array.isArray(stage.tasks)) {
        stage.tasks.forEach((task) => {
          if (task?.id) {
            contractorWorkLookup.set(
              task.id,
              hasContractorWork(task.subcontractorTotal_Cost, task.materialTotal_Cost),
            );
          }
        });
      }

      if (Array.isArray(stage.stages)) {
        stage.stages.forEach(visitStage);
      }
    };

    stages.forEach(visitStage);

    return contractorWorkLookup;
  };

  const buildProjectContractorWorkLookup = ({ project = {}, options = [] }) => {
    const contractorWorkLookup = buildContractorWorkLookup(
      combineData({ project, variation: options }) || [],
    );

    (options || []).forEach((option) => {
      if (option?.id) {
        contractorWorkLookup.set(
          option.id,
          hasContractorWork(option.subcontractorTotal_Cost, option.materialTotal_Cost),
        );
      }
    });

    return contractorWorkLookup;
  };

  const appendContractorWorkFlags = (options, contractorWorkLookup) =>
    (options || []).map((option) =>
      option.isTask
        ? {
            ...option,
            hasContractorWork: contractorWorkLookup.get(option.value) || false,
          }
        : option,
    );

  // --- State and Redux hooks ---
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [fields, setFields] = useState(createPOFields);
  // Get category data from Redux store at component level
  const categoryList = useSelector((state) => state.catalog.catalogs) || [];

  const [state, setState] = useState({
    poId: "",
    confirmDialog: false,
    edit: false,
    vendorsList: [],
  });
  const [projectSiteAddress, setProjectSiteAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    suburb: "",
    state: "",
    postcode: "",
    country: "",
  });
  const [officeAddress, setOfficeAddress] = useState({
    street_address: "",
    addressLine2: "",
    suburb: "",
    state: "",
    postcode: "",
    country: "",
  });
  const [addedItemstoPO, setAddedItemsToPO] = useState({
    taskList: [],
    sectionList: [],
  });
  const [selectInputTasks, setSelectInputTasks] = useState({
    value: [],
    all_items: [],
    items: [],
    label: "Select Task",
    name: "tasksList",
    placeholder: "Select Task",
    error: false,
    valid: true,
    touched: false,
    isSearchable: true,
    isMulti: true,
    validation: [ValidatorTypes.OPTIONAL, ValidatorTypes.MULTISELECT],
  });
  const [materialPOData, setMaterialPOData] = useState({
    taskItems: [],
    tasksList: [],
  });
  const [originalSectionData, setOriginalSectionData] = useState([]); // Store original section data for attribution checking
  const [intendedCostAttributionTasks, setIntendedCostAttributionTasks] = useState([]);
  const { poId } = useParams();
  const [itemSelectionModalOpen, setItemSelectionModalOpen] = useState(false);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);

  useEffect(() => {
    init();
  }, []);

  // Set today's date for orderDate only when creating a new PO (not editing)
  useEffect(() => {
    if (!poId && !state.edit && fields.orderDate.value === "") {
      setFields((prev) => ({
        ...prev,
        orderDate: { ...prev.orderDate, value: new Date() },
      }));
    }
  }, [poId, state.edit]);

  /**
   * Initializes the PO creation/editing form: loads vendors, project data, PO data (if editing), and global settings.
   */
  const init = async () => {
    try {
      await getVendorList();
      if (projectId && !poId) {
        await projectChangeHandler(projectId);
      } else {
        await getProjectList();
      }
      if (poId) await getPO();
      await getGlobalSetting();
      await loadSavedState();
    } catch (error) {
      props.warningSnackBar(
        "Failed to load initial data. Please refresh the page.",
      );
    }
  };
  const loadSavedState = async () => {
    // Loads saved PO state from localStorage (for vendor edit return or page reload)
    const savedState = localStorage.getItem("createPOState");
    if (savedState) {
      const { fields: savedFields, state: savedPOState } =
        JSON.parse(savedState);
      setFields(savedFields);
      setState(savedPOState);
      localStorage.removeItem("createPOState");
      const vendorEditFlag = localStorage.getItem("vendorEditReturn");
      if (vendorEditFlag) {
        const vendorsList = await getVendorList();
        const vendor = vendorsList.find(
          (item) => item.id === savedFields.vendor.value,
        );
        if (vendor) {
          setFields((prev) => ({
            ...prev,
            street_address: {
              ...prev.street_address,
              value: vendor.street_address || "",
              error: false,
              valid: true,
            },
            suburb: {
              ...prev.suburb,
              value: vendor.suburb || "",
              error: false,
              valid: true,
            },
            state: {
              ...prev.state,
              value: vendor.state || "",
              error: false,
              valid: true,
            },
            postcode: {
              ...prev.postcode,
              value: vendor.postcode || "",
              error: false,
              valid: true,
            },
            country: {
              ...prev.country,
              value: vendor.country || "",
              error: false,
              valid: true,
            },
          }));
        } else {
          props.warningSnackBar("Vendor not found");
        }
        localStorage.removeItem("vendorEditReturn");
      }
    }
  };

  const getPO = async () => {
    // Loads PO data for editing, sets form fields and state accordingly
    const query = `?poId=${poId}`;
    let res = await props.getPurchaseOrder(query);

    let tasksList = res.items.map((item) => ({
      label: item.taskName,
      value: item.taskId || item.stageId, // Handle both taskId and stageId
      taskDescription: item.taskDescription,
      taskType: item.taskType,
      stageId: item.stageId,
      isStage: item.taskType === "STAGE", // Add isStage flag for proper attribution
    }));

    setFields((prev) => ({
      ...prev,
      quote_no: { ...prev.quote_no, value: res.quoteNo || "" },
      project: { ...prev.project, value: res.projectId || "" },
      intendedCostAttribution: {
        ...prev.intendedCostAttribution,
        value: res.workAssociationTaskId || res.workAssociationStageId || "",
      },
      tasksList: {
        ...prev.tasksList,
        value: _.compact(
          _.uniq(res.items.map((item) => item.taskId || item.stageId)),
        ),
      },
      vendor: { ...prev.vendor, value: res.vendorId || "" },
      deliveryOption: {
        ...prev.deliveryOption,
        value: res.deliveryOption || "",
      },
      deliveryDate: { ...prev.deliveryDate, value: res.deliveryDate || "" },
      orderDate: { ...prev.orderDate, value: res.createdAt || "" },

      notes: { ...prev.notes, value: res.notes || "" },
      street_address: {
        ...prev.street_address,
        value: res.street_address || "",
      },
      suburb: { ...prev.suburb, value: res.suburb || "" },
      state: { ...prev.state, value: res.state || "" },
      postcode: { ...prev.postcode, value: res.postcode || "" },
      country: { ...prev.country, value: res.country || "" },
    }));

    setState((prev) => ({
      ...prev,
      taskItems: res.items || [],
      poId: res.u_id,
      uPoId: res.id,
      edit: true,
    }));

    setMaterialPOData((prev) => ({
      ...prev,
      taskItems: res.items || [],
      tasksList: _.uniqBy(tasksList, "value"),
    }));

    if (!projectId) await getProjectList();
    /**
     *
     * Extracts all valid task IDs from PO items to pass to filterLockedSections.
     * Filters out null/undefined to ensure only valid IDs are used for historical task preservation.
     */
    const currentTaskIds = (res.items || [])
      .map((item) => item.taskId)
      .concat(res.workAssociationTaskId, res.workAssociationStageId)
      .filter((id) => id !== null && id !== undefined && id !== ""); // Remove invalid values
    await projectChangeHandler(res.projectId, currentTaskIds);
  };

  const getProjectList = async () => {
    // Loads and formats available projects for selection
    // let poCount;
    let list = await props.getProjectList(null, "PROJECT");
    // if (!poId) poCount = await props.getPoCount();

    let projects = [];
    list &&
      list.forEach(({ name, id }) =>
        projects.push({ key: name.trim(), value: id }),
      );
    projects = sortByKeyAsc(projects);

    setFields((prev) => ({
      ...prev,
      project: {
        ...prev.project,
        items: projects,
        ...(projectId ? { value: projectId } : {}),
      },
    }));
    setState((prev) => ({
      ...prev,
      poId: poId ? poId : undefined, //poCount && poCount.maxId + 1,
    }));
  };

  const getGlobalSetting = async () => {
    // Loads office address from global settings (for autofill)
    const { data } = await props.getGlobalSetting("LETTERHEAD");
    if (!data.address) return;
    const _address = JSON.parse(data.address);
    setOfficeAddress((prev) => ({ ...prev, ..._address }));
  };

  const getVendorList = async () => {
    // Loads and formats available vendors for selection
    const vendorsList = await props.getVendorList("Supplier");
    const vendors =
      (Array.isArray(vendorsList) &&
        vendorsList.map(({ id, businessName }) => ({
          key: businessName,
          value: id,
        }))) ||
      [];
    setFields((prev) => ({
      ...prev,
      vendor: { ...prev.vendor, items: vendors },
      deliveryOption: { ...prev.deliveryOption, items: deliveryOptions },
    }));
    setState((prev) => ({
      ...prev,
      vendorsList,
    }));
    return vendorsList;
  };

  const addVariationNumbersToTasks = (stages) => {
    if (!Array.isArray(stages)) return stages;

    // Find the variations section
    const variationsSection = stages.find((stage) => stage.id === "variations");

    if (
      variationsSection &&
      variationsSection.tasks &&
      Array.isArray(variationsSection.tasks)
    ) {
      const processedVariations = getVariationNumber(variationsSection.tasks);

      processedVariations.forEach((variation) => {
        if (variation.variationNo && variation.selected) {
          variation.name = `${variation.variationNo} ${variation.name}`;
        }
      });

      variationsSection.tasks = processedVariations.filter(
        (variation) => 
          variation.optionProposalStatus === "APPROVED" || 
          variation.optionProposalStatus === "PENDING"
      );
    }

    return stages;
  };

  const projectChangeHandler = async (value, alwaysIncludeTaskIds = []) => {
    /**
     * Handles project selection change: loads tasks, stages, and site address for the selected project.
     * Updates dropdowns and autofills address fields as needed.
     */
    setSelectInputTasks((prev) => ({
      ...prev,
      value: [],
      items: [],
      all_items: [],
    }));

    const options = await getOptionsData({ projectId: value, dispatch });
    const project = await props.getProject(value);

    // Get tasks for dropdown display using getTaskdropdownData
    const dropdownResponse = await dispatch(getTaskdropdownData(value));

    // Add variation numbers to tasks before formatting
    const dropdownResponseWithVariationNumbers = addVariationNumbersToTasks(
      dropdownResponse || [],
    );
    const contractorWorkLookup = buildProjectContractorWorkLookup({
      project,
      options,
    });

    /**
     * PO Creation Task Selection Logic
     *
     * BEHAVIOR:
     * - Main TaskMultiSelect dropdown: Shows ALL sections and ALL tasks (flags ignored)
     * - AddMaterialPO component: Uses original behavior (flags respected)
     *   → Sections with is_attribution_level: true show only the section (no children)
     *   → Sections with is_attribution_level: false show all children normally
     * - When selecting tasks from sections with is_attribution_level: true:
     *   → Attribution automatically uses stage ID instead of task ID
     *   → Task remains visible and selectable in the main dropdown
     *
     * BACKWARD COMPATIBILITY:
     * - When editing existing PO (getPO() at line 232), currentTaskIds are extracted
     *   to ensure previously-selected tasks remain visible (though all tasks are shown anyway)
     * - When creating new PO, all tasks are visible from the start
     *
     * @see handleTaskChange() - handles automatic stage attribution logic
     */
    // PO Creation: Show all tasks (no filtering) but handle attribution automatically
    // Unlike WO creation which hides tasks in flagged sections
    /**
     * Removes is_attribution_level flags from section data for dropdown display.
     *
     * PURPOSE: Ensures the main TaskMultiSelect dropdown shows ALL sections and ALL tasks,
     * ignoring the attribution level flags that would normally hide children in flagged sections.
     *
     * USAGE: Applied to data before passing to convertToReactSelectFormat() for the main dropdown.
     * This creates maximum visibility while preserving the original data for attribution logic.
     *
     * @param {Array} data - Hierarchical section/stage data with potential is_attribution_level flags
     * @returns {Array} Same structure but with all is_attribution_level flags set to false
     */
    const removeAttributionFlags = (data) => {
      if (!Array.isArray(data)) return [];

      return data.map((section) => ({
        ...section,
        is_attribution_level: section.is_attribution_level,
        isHistoricalView: !!section.is_attribution_level,
        stages: section.stages ? removeAttributionFlags(section.stages) : [],
      }));
    };

    /**
     * Apply flag removal to create data for main dropdown (shows everything).
     * This ensures users can see and select any task regardless of section attribution level.
     */
    const filteredData = removeAttributionFlags(
      dropdownResponseWithVariationNumbers || [],
    );

    /**
     * Store original section data (with flags intact) for attribution checking.
     *
     * PURPOSE: The handleTaskChange function needs the original flags to determine
     * when a task comes from an attribution-level section and should use stage attribution.
     *
     * USAGE: Referenced by findParentSection() in handleTaskChange to check is_attribution_level.
     */
    const formattedIntendedCostAttribution = getTimesheetSectionDropdownOptions(
      dropdownResponseWithVariationNumbers || [],
      alwaysIncludeTaskIds,
    );
    setIntendedCostAttributionTasks(formattedIntendedCostAttribution);

    // Store raw hierarchy (flags intact) for findNearestFlaggedAncestor / getDescendantStageIds lookups.
    setOriginalSectionData(dropdownResponseWithVariationNumbers || []);

    /**
     * Format data for main TaskMultiSelect dropdown.
     *
     * RESULT: Shows ALL sections and ALL tasks (flags ignored via removeAttributionFlags).
     * Users can see every available task and select anything.
     * allowSectionSelection=true makes sections selectable with descendant IDs.
     */
    const formatted = appendContractorWorkFlags(
      convertToReactSelectFormat(
        filteredData,
        null,
        true,
        true,
      ),
      contractorWorkLookup,
    );

    /**
     * Create separate formatted data for AddMaterialPO component with original flag behavior.
     *
     * PURPOSE: AddMaterialPO should respect the original attribution logic where
     * sections with is_attribution_level: true show only the section (no children).
     *
     * USAGE: Passed to materialPOData.tasksList and consumed by AddMaterialPO component.
     * This maintains the traditional behavior for material selection.
     *
     * RESULT: Flagged sections show as selectable options without their children,
     * maintaining the original user experience for material management.
     */
    const formattedForMaterialPO = convertToReactSelectFormat(
      dropdownResponseWithVariationNumbers || [],
      null,
      true,
      true, // allowSectionSelection: so stage options have value = stage.id for inline dropdown match and correct badge
    );
    /**
     * Post-process formattedForMaterialPO to disable selections of unflagged sections.
     *
     * PURPOSE: In the item table dropdown, we only want users to select:
     * 1. Tasks (obviously)
     * 2. Sections that are Attribution Levels (because they act as aggregate tasks)
     *
     * We do NOT want users selecting intermediate sections that are NOT attribution levels,
     * as this creates ambiguity in attribution.
     */
    const restrictedFormattedForMaterialPO = formattedForMaterialPO.map(
      (option) => {
        // If it's a section/stage but NOT an attribution level, disable it
        if (
          (option.isSection || option.isStage) &&
          !option.isAttributionLevel
        ) {
          return { ...option, isDisabled: true };
        }
        return option;
      },
    );

    // Get tasks with materials using getStages (like original implementation)
    const stages = await dispatch(getStages(value));
    let tasksList = [],
      tasksWithMaterials = [];

    Array.isArray(stages) &&
      stages.forEach(({ tasks, id, name, description, hasStages }) => {
        tasksWithMaterials.push(...tasks);
        Array.isArray(tasks) &&
          tasks.forEach(
            ({ id, name, subTasks, description, material, is_variation }) => {
              tasksList.push({
                key: name,
                value: id,
                taskDescription: description,
                taskType: TaskType.TASK,
              });
            },
          );
      });

    tasksWithMaterials.push(...options);
    Array.isArray(options) &&
      options.forEach(({ id, name, description }) => {
        tasksList.push({
          key: name,
          value: id,
          taskDescription: description,
          taskType: TaskType.VARIATION,
        });
      });
    setFields((prev) => ({
      ...prev,
      project: {
        ...prev.project,
        value: value,
        error: false,
        valid: true,
        ...(projectId && project?.name
          ? { items: [{ key: project.name, value: value }] }
          : {}),
      },
      tasksList: { ...prev.tasksList, value: "", items: [] },
    }));
    setState((prev) => ({
      ...prev,
      tasksList: [],
      taskItems: [],
      tasks: tasksWithMaterials, // Store tasks with materials for getMaterialsList
      selectedItems: [],
      stages: stages, // Store stages for material extraction
    }));

    /**
     * Update main TaskMultiSelect dropdown state with flag-free data.
     *
     * PURPOSE: Provides maximum visibility - users can see and select any task.
     * The 'items' array contains all sections and tasks with flags removed.
     * The 'all_items' preserves original task data for material extraction.
     */
    setSelectInputTasks((prev) => ({
      ...prev,
      items: [{ label: "Select All", value: "select_all" }, ...formatted],
      all_items: tasksWithMaterials, // Store tasks with materials for getMaterialsList
    }));

    /**
     * Update AddMaterialPO component state with original flag behavior.
     *
     * PURPOSE: Maintains traditional material selection experience where
     * attribution-level flags control visibility of section children.
     *
     * USAGE: This data is passed to AddMaterialPO component via tasksList prop.
     * Flagged sections will show only the section option, not their children.
     */
    setMaterialPOData((prev) => ({
      ...prev,
      tasksList: restrictedFormattedForMaterialPO, // Use restricted list for AddMaterialPO
    }));

    const siteAddress = await props.getCustomerSiteAddress(project.customer.id);
    const _siteAddress = siteAddress.find((_) => {
      let regex = /[,-\/& .]/g;
      let fullAddress1 = `${_.addressLine1 || ""}${_.addressLine2 || ""}${_.suburb || ""
        }${_.state || ""}${_.country || ""}${_.postcode || ""}`;
      let fullAddress2 = `${_.addressLine1 || ""}${_.addressLine2 || ""}${_.suburb || ""
        }${_.state || ""}${_.postcode || ""}${_.country || ""}`;
      const _fullAddress1 = fullAddress1
        .toString()
        .replaceAll(regex, "")
        .toLowerCase();
      const _fullAddress2 = fullAddress2
        .toString()
        .replaceAll(regex, "")
        .toLowerCase();
      const Projectaddress = project.address
        .toString()
        .replaceAll(regex, "")
        .toLowerCase();
      const addressName = _.name.replaceAll(regex, "").toLowerCase();
      return (
        Projectaddress == addressName ||
        Projectaddress == _fullAddress1 ||
        Projectaddress == _fullAddress2
      );
    });
    setProjectSiteAddress((prev) => ({ ...prev, ..._siteAddress }));
    if (fields.deliveryOption.value === "SITE" && fields.vendor.value)
      updateAddress(_siteAddress);
  };

  const updateAddress = ({
    addressLine1,
    addressLine2,
    address,
    street_address,
    suburb,
    state,
    postcode,
    country,
  }) => {
    // Updates address fields in the form (for site, office, or vendor address)
    setFields((prev) => ({
      ...prev,
      street_address: {
        ...prev.street_address,
        value: street_address || [addressLine1, addressLine2].join(" "),
      },
      suburb: { ...prev.suburb, value: suburb?.value || suburb },
      state: { ...prev.state, value: state?.value || state },
      postcode: { ...prev.postcode, value: postcode?.value || postcode },
      country: { ...prev.country, value: country?.value || country },
    }));
  };

  const setDeliveryOption = async (value) => {
    // Handles delivery option change and updates address fields accordingly
    if (value === "SITE") updateAddress(projectSiteAddress);
    if (value === "PICKUP") updateVendorAddress(fields.vendor.value);
    if (value === "OFFICE") updateAddress(officeAddress);
  };

  const changeHandler = async (type, value) => {
    // Handles generic field changes and triggers delivery option logic if needed
    setFields((prev) => ({
      ...prev,
      [type]: { ...prev[type], value: value, error: false, valid: true },
    }));
    await setDeliveryOption(value);
  };

  const updateVendorAddress = (value, type) => {
    // Updates address fields based on selected vendor
    let { vendorsList = [] } = state;
    let vendor = vendorsList.find((item) => item.id == value) || {};
    setFields((prev) => ({
      ...prev,
      vendor: { ...prev.vendor, value: value, error: false, valid: true },
      street_address: {
        ...prev.street_address,
        value: vendor.street_address || "",
        error: false,
        valid: true,
      },
      suburb: {
        ...prev.suburb,
        value: vendor.suburb || "",
        error: false,
        valid: true,
      },
      state: {
        ...prev.state,
        value: vendor.state || "",
        error: false,
        valid: true,
      },
      postcode: {
        ...prev.postcode,
        value: vendor.postcode || "",
        error: false,
        valid: true,
      },
      country: {
        ...prev.country,
        value: vendor.country || "",
        error: false,
        valid: true,
      },
      ...(type
        ? {
          [fields.deliveryOption.name]: {
            ...prev[fields.deliveryOption.name],
            value: "SITE",
            error: false,
            valid: true,
          },
        }
        : {}),
    }));
  };

  const vendorChangeHandler = (value) => {
    // Handles vendor selection change and updates address fields
    if (fields.deliveryOption.value !== "SITE")
      updateVendorAddress(value, "SITE");
    else updateVendorAddress(value);
  };

  const submitForm = async (selectedItems) => {
    /**
     * Validates the form and selected items, shows confirmation dialog if needed, and triggers PO creation.
     */
    let isFormValid = true;
    let isConfirmDialog = false;
    let _fields = _.cloneDeep(fields);
    for (let key in _fields) {
      const { value, validation } = fields[key];
      const { error, valid } = ValidateField({ value, validation });
      _fields[key].touched = true;
      _fields[key].valid = valid;
      _fields[key].error = error;
      isFormValid = isFormValid && valid;
    }

    if (
      selectedItems.length === 0 ||
      (selectedItems.selectedItems && selectedItems.selectedItems.length === 0)
    ) {
      isConfirmDialog = true;
    }

    if (!isFormValid) {
      setFields(_fields);
      setState((prev) => ({
        ...prev,
        selectedItems,
      }));
    } else if (isConfirmDialog) {
      setState((prev) => ({
        ...prev,
        confirmDialog: isConfirmDialog,
        selectedItems,
      }));
    } else {
      setFields(_fields);
      setState((prev) => ({
        ...prev,
        selectedItems,
      }));
      await createPO(selectedItems);
    }
  };

  const createPO = async (selectedItems) => {
    // Submits the PO data to backend (create or edit)
    let { edit } = state;
    let itemsToSubmit = [];
    if (Array.isArray(selectedItems)) {
      itemsToSubmit = _.cloneDeep(selectedItems);
      itemsToSubmit.forEach((item) => {
        item.costPerItem = item.costPerItem.value;
        item.name = item.name.value;
        item.qty = parseFloat(item.qty.value || 0);
        item.total = item.total.value;
        item.unit = item.unit.value;
      });
    }

    const selectedAttributionId = fields.intendedCostAttribution?.value || "";
    let workAssociationTaskId = undefined;
    let workAssociationStageId = undefined;
    let workAssociationTaskType = undefined;

    if (selectedAttributionId) {
      let selectedOpt = null;
      for (const opt of intendedCostAttributionTasks) {
        if (opt.value === selectedAttributionId) {
          selectedOpt = opt;
          break;
        }
        if (Array.isArray(opt.options)) {
          const match = opt.options.find((sub) => sub.value === selectedAttributionId);
          if (match) {
            selectedOpt = match;
            break;
          }
        }
      }

      if (selectedOpt && (selectedOpt.isStage || selectedOpt.taskType === "STAGE")) {
        workAssociationStageId = selectedAttributionId;
        workAssociationTaskType = "STAGE";
      } else {
        workAssociationTaskId = selectedAttributionId;
        workAssociationTaskType = "TASK";
      }
    }

    const data = {
      vendorId: fields.vendor.value,
      items: itemsToSubmit,
      workAssociationTaskId,
      workAssociationStageId,
      workAssociationTaskType,
      deliveryOption: fields.deliveryOption.value || undefined,
      deliveryDate: fields.deliveryDate.value || undefined,
      createdAt: fields.orderDate.value,
      quoteNo: fields.quote_no.value,
      notes: fields.notes.value,
      street_address: fields.street_address.value,
      suburb: fields.suburb.value,
      state: fields.state.value,
      postcode: fields.postcode.value,
      country: fields.country.value,
    };

    data.projectId = fields.project.value;
    if (edit) {
      await props.editPurchaseOrder(
        data,
        poId,
        "Purchase Order Updated Successfully",
      );
    } else {
      await props.savePurchaseOrder(
        data,
        "",
        "Purchase Order Created Successfully",
      );
    }

    props.cancelHandler();
    setState((prev) => ({
      ...prev,
      confirmDialog: false,
    }));
  };

  /**
   * Builds a composite key for a PO line item to uniquely identify an (item, task) pair.
   * Falls back to id-only when taskId is absent (e.g. inline items).
   * @param {{ id: string|number, taskId?: string, stageId?: string, originalTaskId?: string }} item
   * @returns {string}
   */
  const poItemKey = (item) =>
    `${String(item.id)}_${String(item.taskId || item.stageId || item.originalTaskId || "")}`;

  /**
   * Resolves a raw material item's taskId to the nearest attribution-level section.
   * @param {{ taskId?: string, stageId?: string }} item
   * @returns {object} Item with taskId/taskName/taskType updated to attributed section.
   */
  const applyAttribution = (item) => {
    const originalTaskId = item.taskId || item.stageId || "";
    const ancestor = originalTaskId ? findNearestFlaggedAncestor(originalTaskId) : null;
    return ancestor
      ? { ...item, taskId: ancestor.id, taskName: ancestor.path, taskType: "STAGE" }
      : item;
  };

  /**
   * Aggregates an array of items by key, summing `qty` for matching entries.
   * @param {object[]} items
   * @param {function(object): string} keyFn
   * @returns {object[]}
   */
  const aggregateByKey = (items, keyFn) =>
    Object.values(
      items.reduce((acc, item) => {
        const key = keyFn(item);
        acc[key] = acc[key]
          ? { ...acc[key], qty: toDecimal(acc[key].qty).plus(toDecimal(item.qty)).toNumber() }
          : { ...item };
        return acc;
      }, {})
    );

  const addItemsHandler = () => {
    /**
     * Adds all items from selected tasks to the PO, using getMaterialsList for extraction.
     * Updates materialPOData and addedItemstoPO state with proper attribution handling.
     */
    let isFormValid = true;

    // Remove section validation since we're removing sections
    const { error, valid } = ValidateField({
      value: selectInputTasks.value,
      validation: selectInputTasks.validation,
    });
    setSelectInputTasks((prev) => ({
      ...prev,
      error,
      valid,
      touched: true,
    }));
    isFormValid = isFormValid && valid;

    if (!isFormValid) return "";

    // Use the tasks with materials from state.tasks (like original implementation)
    let tasks = selectInputTasks.all_items || [];
    let selectedTaskIds = selectInputTasks.value.map((option) => option.value);
    let selectedTasks = (selectInputTasks.items || []).filter((item) =>
      selectedTaskIds.includes(item.value),
    );

    // Extract materials using getMaterialsList for each selected task
    const taskDetails = selectedTasks.map((task) => {
      if (task && (task.isStage || task.isAttributionLevel)) {
        return {
          ...task,
          descendantStageIds: getDescendantStageIds(
            originalSectionData,
            task.value,
          ),
        };
      }
      return task;
    });

    let materials = taskDetails
      .flatMap((task) => {
        const result = getMaterialsList(tasks, task);
        return result.status ? result.data : [];
      })
      .filter((item) => item);

    if (materials && materials.length > 0) {
      // Process materials with attribution logic
      const processedMaterials = materials.map((material) => {
        // Find the corresponding selected task/section for this material
        const selectedTask = selectInputTasks.value.find((selectedOption) => {
          // Match by original task ID or current task ID
          if (
            selectedOption.originalTaskId === material.originalTaskId ||
            selectedOption.value === material.originalTaskId
          ) {
            return true;
          }
          // For section selections, check if material's task is in childTaskIds
          if (selectedOption.isSection && selectedOption.childTaskIds) {
            return selectedOption.childTaskIds.includes(
              material.originalTaskId,
            );
          }
          return false;
        });

        // When user selected a task under an attribution-level section, use that section for the row (not the task)
        if (selectedTask && selectedTask.attributionId) {
          const flaggedAncestor = findNearestFlaggedAncestor(
            selectedTask.attributionId,
          );
          if (flaggedAncestor) {
            return {
              ...material,
              stageId: flaggedAncestor.id,
              taskId: flaggedAncestor.id,
              taskName: flaggedAncestor.path,
              taskType: "STAGE",
            };
          }
        }

        // Else: use material's stage in hierarchy to find nearest flagged ancestor (e.g. when section was selected)
        const flaggedAncestor = findNearestFlaggedAncestor(
          material.originalStageId,
        );
        if (flaggedAncestor) {
          return {
            ...material,
            stageId: flaggedAncestor.id,
            taskId: flaggedAncestor.id,
            taskName: flaggedAncestor.path,
            taskType: "STAGE",
          };
        }

        // No flagged section -> fallback to task level
        return {
          ...material,
          stageId: null,
          taskId: material.originalTaskId,
          taskName: material.originalTaskName,
          taskType: "TASK",
        };
      });


      // Aggregate: same catalog item under the same attributed section → one row with summed qty.
      const aggregatedMaterials = aggregateByKey(processedMaterials, poItemKey);

      /**
       * DUPLICATE PREVENTION: Check against existing PO line items by composite key (id + taskId).
       * Same catalog item can appear under different tasks with different quantities — the composite
       * key ensures each (item, task) combination is treated as a unique PO row.
       */
      const existingItemIds = new Set(
        (materialPOData.taskItems || []).map(poItemKey)
      );

      const newMaterials = aggregatedMaterials.filter(
        (item) => !existingItemIds.has(poItemKey(item))
      );

      if (newMaterials.length === 0) {
        props.warningSnackBar("All selected items are already in the PO list.");
        return;
      }

      // Note: Don't modify tasksList - the dropdown should maintain its original options
      // Append only new (non-duplicate) items to the existing PO list
      setMaterialPOData((prev) => ({
        ...prev,
        taskItems: [...(prev.taskItems || []), ...newMaterials],
      }));

      setAddedItemsToPO((prevState) => ({
        ...prevState,
        taskList: [...prevState.taskList, ...selectedTasks],
      }));
    } else {
      props.warningSnackBar("No materials found for the selected tasks.");
      return;
    }
  };

  const handleTaskSelectAll = () => {
    // Selects all available tasks in the dropdown
    const allTasks = selectInputTasks.items
      .filter(
        (task) =>
          task.value !== "select_all" &&
          !task.value.toString().startsWith("section-"),
      )
      .map((task) => ({ label: task.label, value: task.value }));
    setSelectInputTasks((prev) => ({
      ...prev,
      value: allTasks,
    }));
    changeHandler("tasksList", allTasks);
  };

  /**
   * Finds the parent section of a task using the original section data.
   *
   * PURPOSE: Determines if a selected task belongs to a section with is_attribution_level: true.
   * This is crucial for automatic stage attribution logic.
   *
   * USAGE: Called by handleTaskChange() when processing task selections.
   * Uses originalSectionData (stored with flags intact) to check attribution levels.
   *
   * @param {string} stagePath - Path like "Section Name > SubSection Name" or "Section Name"
   * @param {Array} dropdownItems - Current dropdown items (not used in this function)
   * @returns {Object|null} Parent section object or null if not found
   */
  const findParentSection = (stagePath, dropdownItems) => {
    // Find the immediate parent section (the stage that contains the task) by walking the full path.
    // stagePath is e.g. "Section 1 > Sewer > Drainage" or "BASEMENT" for the stage that contains the task.
    // We need the last segment (the immediate parent), not the first, so nested attribution works at any depth.
    const pathParts = stagePath
      .split(" > ")
      .map((p) => p.trim())
      .filter(Boolean);
    if (pathParts.length === 0) return null;

    const findSectionByPath = (sections, names, index) => {
      if (!Array.isArray(sections) || index >= names.length) return null;
      const targetName = names[index];
      for (const section of sections) {
        if (section.name === targetName) {
          if (index === names.length - 1) return section;
          const found = findSectionByPath(
            section.stages || [],
            names,
            index + 1,
          );
          if (found) return found;
        }
      }
      return null;
    };

    return findSectionByPath(originalSectionData, pathParts, 0);
  };

  /**
   * Finds a task's full path in the hierarchy by its ID.
   * Recursively searches through stages and tasks to build the complete path.
   *
   * @param {string} taskId - The task or stage ID to find
   * @returns {Object|null} { path: "Section 1 > Sewer > Drainage", sections: [...] } or null
   */
  const findTaskPathById = (taskId) => {
    if (!taskId || !originalSectionData) return null;

    const searchInHierarchy = (items, currentPath, ancestors) => {
      for (const item of items) {
        const newPath = currentPath
          ? `${currentPath} > ${item.name}`
          : item.name;
        const newAncestors = [...ancestors, item];

        // Check if this is the target (either a stage or has matching id)
        if (item.id === taskId) {
          return { path: newPath, sections: newAncestors };
        }

        // Check in child stages
        if (item.stages && item.stages.length > 0) {
          const found = searchInHierarchy(item.stages, newPath, newAncestors);
          if (found) return found;
        }

        // Check in tasks under this stage
        if (item.tasks && item.tasks.length > 0) {
          for (const task of item.tasks) {
            if (task.id === taskId) {
              const taskPath = `${newPath} > ${task.name}`;
              return { path: taskPath, sections: newAncestors, task };
            }
            // Check subtasks
            if (task.subTasks && task.subTasks.length > 0) {
              for (const subTask of task.subTasks) {
                if (subTask.id === taskId) {
                  const subTaskPath = `${newPath} > ${task.name} > ${subTask.name}`;
                  return {
                    path: subTaskPath,
                    sections: newAncestors,
                    task: subTask,
                  };
                }
              }
            }
          }
        }
      }
      return null;
    };

    return searchInHierarchy(originalSectionData, "", []);
  };

  /**
   * Finds the nearest flagged ancestor for a given task ID or path.
   * Traverses up the hierarchy to find the first section with is_attribution_level=true.
   *
   * PURPOSE: Implements the "Nearest Flagged Ancestor" rule for cost attribution.
   * When a section is selected, costs should be attributed to the nearest parent
   * that has is_attribution_level=true. If no flagged ancestor exists, returns null.
   *
   * @param {string} stagePathOrTaskId - Path like "Section 1 > Sub Section 1A" OR a task ID
   * @returns {Object|null} Nearest flagged ancestor section or null
   */
  const findNearestFlaggedAncestor = (stagePathOrTaskId) => {
    if (!stagePathOrTaskId || !originalSectionData) return null;

    let sections = [];

    // Check if input looks like a UUID (task ID) vs a path
    const isUUID =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        stagePathOrTaskId,
      );

    if (isUUID) {
      // Find the task's location in hierarchy by ID
      const taskLocation = findTaskPathById(stagePathOrTaskId);
      if (taskLocation) {
        sections = taskLocation.sections;
      }
    } else {
      // Parse path and find sections
      const pathParts = stagePathOrTaskId.split(" > ");
      const findSectionByName = (items, name) => {
        for (const item of items) {
          if (item.name === name) return item;
          if (item.stages && item.stages.length > 0) {
            const found = findSectionByName(item.stages, name);
            if (found) return found;
          }
        }
        return null;
      };

      for (const part of pathParts) {
        const section = findSectionByName(originalSectionData, part);
        if (section) sections.push(section);
      }
    }

    // Find the nearest flagged ancestor from the sections
    let flaggedAncestor = null;
    let currentPath = "";

    for (const section of sections) {
      currentPath = currentPath
        ? `${currentPath} > ${section.name}`
        : section.name;
      if (section.is_attribution_level || section.isAttributionLevel) {
        flaggedAncestor = {
          id: section.id,
          name: section.name,
          path: currentPath,
          is_attribution_level: true,
        };
      }
    }

    return flaggedAncestor;
  };

  /**
   * Collects all descendant stage IDs for a given stage ID.
   *
   * @param {Array} sections - Hierarchical stage data
   * @param {string} targetStageId - Stage ID to collect descendants for
   * @returns {Array} Array of descendant stage IDs (including the target stage)
   */
  const getDescendantStageIds = (sections, targetStageId) => {
    if (!Array.isArray(sections) || !targetStageId) return [];

    const collectedIds = new Set();

    const collectAll = (stage) => {
      if (!stage) return;
      collectedIds.add(stage.id);
      if (Array.isArray(stage.stages)) {
        stage.stages.forEach(collectAll);
      }
    };

    const findAndCollect = (stages) => {
      for (const stage of stages) {
        if (stage.id === targetStageId) {
          collectAll(stage);
          return true;
        }
        if (Array.isArray(stage.stages) && findAndCollect(stage.stages)) {
          return true;
        }
      }
      return false;
    };

    findAndCollect(sections);
    return Array.from(collectedIds);
  };

  /**
   * Handles task selection changes with automatic stage attribution logic.
   *
   * PURPOSE: Processes user task selections and automatically applies stage attribution
   * when tasks are selected from sections with is_attribution_level: true.
   *
   * BEHAVIOR:
   * - Normal tasks: Keep task-level attribution (taskType: TASK)
   * - Tasks from attribution-level sections: Convert to stage-level attribution (taskType: STAGE)
   *
   * USAGE: Called when user selects/deselects tasks in the TaskMultiSelect dropdown.
   * The function checks each selected task against originalSectionData to determine
   * if automatic stage attribution should be applied.
   *
   * @param {Array} selectedOptions - Selected task options from the dropdown
   */
  const handleTaskChange = (selectedOptions) => {
    // Handles task selection change with attribution logic for sections with is_attribution_level
    const selectedValues = selectedOptions.map((option) => option.value);
    if (selectedValues.includes("select_all")) handleTaskSelectAll();
    else {
      // Process selected options to handle attribution logic
      const processedOptions = selectedOptions.map((option) => {
        // If this is a task (not a section) and has stagePath, check if it's from an attribution-level section
        if (option.taskType && option.stagePath) {
          // Find the nearest flagged ancestor for this task (using the task's ID or path)
          // We prefer using the task ID (option.value) as it's more reliable for finding the exact position in hierarchy
          const flaggedAncestor = findNearestFlaggedAncestor(option.value);

          // If a flagged ancestor exists, attribute this task to that stage
          if (flaggedAncestor) {
            return {
              ...option,
              // Override taskType to use stage attribution
              taskType: TaskType.STAGE,
              // Store the stage ID instead of task ID for attribution
              attributionId: flaggedAncestor.id,
              attributionName: flaggedAncestor.name,
              originalTaskId: option.value,
              originalTaskName: option.label,
            };
          }
        }
        return option;
      });

      setSelectInputTasks((prev) => ({
        ...prev,
        value: processedOptions,
        error: false,
        valid: true,
        touched: true,
      }));
      changeHandler("tasksList", processedOptions);
    }
  };
  const editVendor = (id) => {
    // Navigates to vendor edit page, saving current PO state
    localStorage.setItem("vendorEditReturn", "true");
    const fromVendorOrders = location.pathname.includes("/vendor/orders");
    if (fromVendorOrders) {
      // Route via vendor wrapper so returnTab is preserved and handled
      const returnFlag = `${location.search ? `${location.search}&` : "?"}returnTo=create-po`;
      navigate(`/${VENDOR}/${VENDOR_ADD_SUPPLIER}/edit/${id}${returnFlag}`);
    } else {
      // Default legacy route when not in vendor/orders context
      navigate(`/${SUPPLIERS}/edit/${id}`);
    }
  };

  const editVendorAddressHandler = () => {
    // Handles edit vendor address action, saving PO state to localStorage
    const vendorId = fields.vendor.value;
    if (!vendorId) return;
    const poData = {
      fields,
      state,
    };
    localStorage.setItem("createPOState", JSON.stringify(poData));
    editVendor(vendorId);
  };

  const vendorHasNoAddress = (vendorId) => {
    // Checks if the selected vendor has no address fields filled
    if (!vendorId) return false;
    const vendor = state.vendorsList.find((item) => item.id === vendorId);
    if (!vendor) return false;
    const fieldsToCheck = [
      vendor.street_address,
      vendor.suburb,
      vendor.state,
      vendor.postcode,
      vendor.country,
      vendor.address,
    ];
    return fieldsToCheck.every((field) => !field || field.trim() === "");
  };

  const openItemSelectionModal = async () => {
    /**
     * Opens the item selection modal and loads available materials for selected tasks.
     * Aggregates materials from tasks and subtasks, and maps categories for display.
     */
    setSelectedItemIds([]);

    try {
      setItemSelectionModalOpen(true);
      setIsLoadingItems(true);

      if (!selectInputTasks.value || selectInputTasks.value.length === 0) {
        props.warningSnackBar("Please select at least one task first");
        setIsLoadingItems(false);
        setItemSelectionModalOpen(false);
        return;
      }

      // Use the tasks with materials from selectInputTasks.all_items
      const tasks = selectInputTasks.all_items || [];
      const selectedTasks = selectInputTasks.value || [];

      const taskDetails = selectedTasks.map((task) => {
        if (task && (task.isStage || task.isAttributionLevel)) {
          return {
            ...task,
            descendantStageIds: getDescendantStageIds(
              originalSectionData,
              task.value,
            ),
          };
        }
        return task;
      });

      const materials = taskDetails
        .flatMap((task) => {
          const result = getMaterialsList(tasks, task);
          return result.status ? result.data : [];
        })
        .filter((item) => item);

      // Apply attribution then aggregate: same item under same section → one row with summed qty.
      const attributedMaterials = materials.map(applyAttribution);
      const uniqueMaterials = aggregateByKey(
        attributedMaterials,
        (item) => `${item.id}_${item.cost || item.price || 0}_${item.taskId || ""}`,
      );


      const materialsWithCategory = uniqueMaterials.map((material) => {
        const category = categoryList.find(
          (cat) => cat.id === material.categoryId,
        );

        return {
          ...material,
          category: {
            name: category ? category.name : "",
            id: material.categoryId || "",
          },
        };
      });

      setAvailableItems(_.orderBy(materialsWithCategory, ["item"], ["asc"]));

      if (uniqueMaterials.length === 0) {
        props.warningSnackBar("No materials found for the selected tasks.");
      }
    } catch (error) {
      props.warningSnackBar("Failed to load materials");
      setAvailableItems([]);
    } finally {
      setIsLoadingItems(false);
    }
  };

  const closeItemSelectionModal = () => {
    // Closes the item selection modal and resets selection
    setItemSelectionModalOpen(false);
    setSelectedItemIds([]);
  };

  const handleItemSelection = (itemId) => {
    // itemId is the composite key: `${id}_${cost}_${taskId}`
    setSelectedItemIds((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const addSelectedItemsToPO = () => {
    /**
     * Adds selected items from the modal to the PO, ensuring no duplicates.
     * Updates materialPOData state and shows warnings for duplicates.
     */
    if (selectedItemIds.length === 0) {
      props.warningSnackBar("Please select at least one item");
      return;
    }

    const itemsToAdd = availableItems.filter((item) =>
      selectedItemIds.includes(
        `${item.id}_${item.cost || item.price || 0}_${item.taskId || ""}`,
      )
    );

    const materialItems = itemsToAdd.map((item) => {
      // Determine the original task ID from the item's related tasks or direct property
      // Use optional chaining for safety, though data structure implies it exists
      const originalTaskId =
        item.relatedTasks?.[0]?.parentTaskId || item.taskId || "";

      // Default to the item's existing task info
      let finalTaskId = originalTaskId;
      // Use existing taskName if available, otherwise it will be empty (or updated by attribution)
      let finalTaskName =
        item.taskName || item.relatedTasks?.[0]?.taskName || "";
      let finalTaskType = item.taskType || TaskType.TASK;

      // Check for hierarchical attribution (Nearest Flagged Ancestor rule)
      const flaggedAncestor = findNearestFlaggedAncestor(originalTaskId);
      if (flaggedAncestor) {
        finalTaskId = flaggedAncestor.id;
        finalTaskName = flaggedAncestor.path;
        finalTaskType = TaskType.STAGE;
      }

      return {
        id: item.id,
        item: item.item || "",
        partNo: item.partNo || "",
        manufacturer: item.manufacturer || "",
        categoryId: item.categoryId || "",
        unit: item.unit || "",
        cost: parseFloat(item.cost || item.price || 0),
        qty: item.qty || 1,
        taskId: finalTaskId,
        taskName: finalTaskName,
        taskType: finalTaskType,
        originalTaskId: originalTaskId, // Preserve original for reference
        isInlineItem: false,
        relatedTasks: item.relatedTasks || [],
      };
    });

    setMaterialPOData((prev) => {
      const existingKeys = new Set(
        prev.taskItems.map(poItemKey)
      );
      const uniqueNewItems = materialItems.filter(
        (item) => !existingKeys.has(poItemKey(item))
      );

      if (uniqueNewItems.length < materialItems.length) {
        const skippedCount = materialItems.length - uniqueNewItems.length;
        props.warningSnackBar(
          `${skippedCount} item(s) were already in the PO and will be skipped.`,
        );
      }

      const updatedItems = [...prev.taskItems, ...uniqueNewItems];
      return {
        ...prev,
        taskItems: updatedItems,
      };
    });

    // Close the modal and reset selection
    closeItemSelectionModal();
  };

  const renderItemSelectionModal = () => {
    // Renders the item selection modal component
    return (
      <ItemSelectionModal
        open={itemSelectionModalOpen}
        onClose={closeItemSelectionModal}
        onAddItems={addSelectedItemsToPO}
        onAddNewItem={() => {
          closeItemSelectionModal();
          setTimeout(() => {
            props.warningSnackBar("Adding new items is not implemented");
          }, 300);
        }}
        onSelectItem={handleItemSelection}
        items={availableItems}
        selectedItemIds={selectedItemIds}
        isLoading={isLoadingItems}
        searchQuery={""}
        onSearchChange={() => { }}
      />
    );
  };

  /**
   * Removes a row from the PO material list by composite key (itemId + taskId).
   * Falls back to id-only removal when taskId is absent (e.g. inline items).
   * @param {string|number} itemId - Catalog item id.
   * @param {string|number} [taskId] - Attribution task/stage id.
   */
  const handleItemRemove = (itemId, taskId) => {
    setMaterialPOData((prev) => {
      const updatedItems = prev.taskItems.filter((item) => {
        const sameId = String(item.id) === String(itemId);
        if (!taskId) return !sameId;
        const sameTask = String(item.taskId || item.stageId) === String(taskId);
        return !(sameId && sameTask);
      });
      return { ...prev, taskItems: updatedItems };
    });
  };

  // const selectedValues = new Set(
  //   (selectInputTasks.value || []).map((option) => option.value),
  // );

  // /**
  //  * Progressive hide: (1) hide selected options, (2) hide headers when all their
  //  * childTaskIds are in selectedValues, (3) hide any option under those headers.
  //  */
  // const headerPathsToHide = new Set();
  // (selectInputTasks.items || []).forEach((item) => {
  //   if (!item) return;
  //   const isHeader = item.isSectionHeader || item.isSection;
  //   const childTaskIds = Array.isArray(item.childTaskIds)
  //     ? item.childTaskIds
  //     : [];
  //   if (!isHeader || childTaskIds.length === 0) return;
  //   const allChildrenSelected = childTaskIds.every((id) =>
  //     selectedValues.has(id),
  //   );
  //   if (allChildrenSelected) {
  //     const path = item.stagePath || item.label || item.displayName;
  //     if (path) headerPathsToHide.add(path);
  //   }
  // });

  // const filteredTaskItems = (selectInputTasks.items || []).filter((item) => {
  //   if (!item) return true;

  //   // Hide selected: do not show selected tasks/sections in the list
  //   if (selectedValues.has(item.value)) return false;

  //   const itemPath = item.stagePath || item.label || item.displayName;
  //   if (itemPath) {
  //     // Hide option if it is under a header whose entire branch is selected
  //     const isUnderHiddenHeader = [...headerPathsToHide].some(
  //       (headerPath) =>
  //         itemPath === headerPath || itemPath.startsWith(`${headerPath} > `),
  //     );
  //     if (isUnderHiddenHeader) return false;
  //   }

  //   return true;
  // });

  // const filteredSelectInputTasks = {
  //   ...selectInputTasks,
  //   items: filteredTaskItems,
  // };

  const filteredSelectInputTasks = selectInputTasks;

  return (
    <>
      <Modal
        open={state.confirmDialog}
        onClose={() => {
          setState((prev) => ({
            ...prev,
            confirmDialog: false,
          }));
        }}
        onClick={createPO}
        modalHeading={"Confirm"}
        modalContent={
          "You are creating an empty Purchase Order. Please return to fill in the details once they are available"
        }
        modalSaveTxt={"Continue"}
        modalCloseTxt="Cancel"
        showActionBtn={true}
        maxWidth={"xs"}
        cxlbtnClass="cancelBtn"
        savebtnClass="createBtn"
        primary
      />
      {renderItemSelectionModal()}
      <div className="purchase-order-block create-po-page" data-cy="create-po">
        <h2 className="content-heading create-po-content-heading">
          {state.edit ? `Edit PO - ${state.uPoId}` : "Create PO"}
        </h2>
        <div className="col-12 breadcrumb-block p-0">{breadcrumb}</div>

        {/* Row 1: Select Project, Vendor, Date Ordered, Delivery/Pickup Date */}
        <div className="row">
          <div className="col-lg-3 col-md-6">
            <SelectInput
              {...fields.project}
              label={"Select Project"}
              onChange={(e) => projectChangeHandler(e.target.value)}
              disabled={projectId}
            />
          </div>
          <div className="col-lg-3 col-md-6">
            <div
              className={`create-po-vendor-wrapper ${fields.deliveryOption.value === "PICKUP" &&
                  vendorHasNoAddress(fields.vendor.value)
                  ? "create-po-vendor-no-address"
                  : ""
                }`}
            >
              <div
                className={
                  fields.deliveryOption.value === "PICKUP" &&
                    vendorHasNoAddress(fields.vendor.value)
                    ? "create-po-vendor-select-with-edit"
                    : "create-po-vendor-select"
                }
              >
                <SelectInput
                  {...fields.vendor}
                  label={"Vendor"}
                  onChange={(e) => vendorChangeHandler(e.target.value)}
                />
              </div>
              {fields.deliveryOption.value === "PICKUP" &&
                vendorHasNoAddress(fields.vendor.value) && (
                  <div
                    className="create-po-edit-icon"
                    onClick={editVendorAddressHandler}
                  >
                    <EditIcon />
                  </div>
                )}
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <DatePickerInput
              {...fields.orderDate}
              label={"Date Ordered"}
              onChange={(e) => changeHandler(fields.orderDate.name, e)}
            />
          </div>
          <div className="col-lg-3 col-md-6">
            <DatePickerInput
              {...fields.deliveryDate}
              label={"Delivery/Pickup Date"}
              onChange={(e) => changeHandler(fields.deliveryDate.name, e)}
            />
          </div>
        </div>

        {/* Rows 2 & 3: Left (Delivery Option, Intended Cost Attribution, Quote No, Street Address) & Right (Notes) */}
        <div className="row mt-3">
          <div className="col-lg-8">
            <div className="row">
              <div className="col-lg-6 col-md-6">
                <SelectInput
                  {...fields.deliveryOption}
                  label={"Delivery Option"}
                  onChange={(e) =>
                    changeHandler(fields.deliveryOption.name, e.target.value)
                  }
                />
              </div>
              <div className="col-lg-6 col-md-6">
                <MultiSelect
                  label="Intended Cost Attribution"
                  placeholder="Select Task"
                  items={intendedCostAttributionTasks}
                  value={(() => {
                    const val = fields.intendedCostAttribution?.value;
                    if (!val || !Array.isArray(intendedCostAttributionTasks)) return null;
                    for (const opt of intendedCostAttributionTasks) {
                      if (opt.value === val) return opt;
                      if (Array.isArray(opt.options)) {
                        const match = opt.options.find((sub) => sub.value === val);
                        if (match) return match;
                      }
                    }
                    return null;
                  })()}
                  onChangeHandler={(selected) =>
                    changeHandler(
                      "intendedCostAttribution",
                      selected ? selected.value : ""
                    )
                  }
                  isStage={true}
                  isMulti={false}
                  isSearchable={true}
                  data-cy="select-intended-cost-attribution"
                />
              </div>
              <div className="col-lg-6 col-md-6 mt-3">
                <CustomInput
                  {...fields.quote_no}
                  onChange={(e) =>
                    changeHandler(fields.quote_no.name, e.target.value)
                  }
                />
              </div>
              <div className="col-lg-6 col-md-6 mt-3">
                <CustomInput
                  {...fields.street_address}
                  onChange={(e) =>
                    changeHandler(fields.street_address.name, e.target.value)
                  }
                />
              </div>
            </div>
          </div>
          <div className="col-lg-4 create-po-notes-column">
            <div className="row">
              <div className="col-12">
                <TextAreaInput
                  {...fields.notes}
                  className="create-po-notes-input"
                  label={"Notes"}
                  onChange={(e) =>
                    changeHandler(fields.notes.name, e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* Row 4: Suburb, State, Post Code, Country */}
        <div className="row mt-3">
          <div className="col-lg-4 col-md-4">
            <CustomInput
              {...fields.suburb}
              onChange={(e) =>
                changeHandler(fields.suburb.name, e.target.value)
              }
            />
          </div>
          <div className="col-lg-3 col-md-3">
            <CustomInput
              {...fields.state}
              onChange={(e) => changeHandler(fields.state.name, e.target.value)}
            />
          </div>
          <div className="col-lg-2 col-md-2">
            <CustomInput
              {...fields.postcode}
              onChange={(e) =>
                changeHandler(fields.postcode.name, e.target.value)
              }
            />
          </div>
          <div className="col-lg-3 col-md-3">
            <CustomInput
              {...fields.country}
              onChange={(e) =>
                changeHandler(fields.country.name, e.target.value)
              }
            />
          </div>
        </div>

        <div className="row mt-2 mb-1">
          <div className="col-lg-8 col-md-7 mt-1 d-flex justify-content-between align-items-start">
            {fields.project.value && selectInputTasks.items.length > 0 && (
              <div className="w-100">
                <TaskMultiSelect
                  label="Add Items From Project"
                  filterVariationsByStatus={true}
                  {...filteredSelectInputTasks}
                  onChangeHandler={handleTaskChange}
                  contractorWorkIcon={ShoppingBasketIcon}
                  dataCy="task-dropdown"
                  staticDropdown={true}
                />
              </div>
            )}
          </div>
          <div className="col-lg-4 col-md-5 mt-3 d-flex align-items-center">
            {selectInputTasks.value.length > 0 && (
              <Button
                variant="contained"
                onClick={addItemsHandler}
                className="create-po-action-button-primary"
                data-cy="add-all-items-to-po-button"
              >
                Add all items to PO
              </Button>
            )}

            {selectInputTasks.value.length > 0 && (
              <Button
                variant="outlined"
                onClick={openItemSelectionModal}
                className="create-po-action-button-outlined"
                data-cy="select-items-button"
              >
                Select Items
              </Button>
            )}
          </div>
        </div>

        <AddMaterialPO
          taskItems={materialPOData.taskItems}
          tasksList={materialPOData.tasksList}
          isProjectPO={true}
          viewPoList={props.cancelHandler}
          submitForm={submitForm}
          isSkippable={true}
          addedItemsPO={addedItemstoPO}
          onItemRemove={handleItemRemove}
          poData={{
            projectId: fields.project.value,
            projectName:
              idx(
                fields.project.items.find(
                  (item) => item.value === fields.project.value,
                ),
                (_) => _.key,
              ) || "",
            vendorId: fields.vendor.value,
            vendorName:
              idx(
                fields.vendor.items.find(
                  (v) => v.value === fields.vendor.value,
                ),
                (_) => _.key,
              ) || "",
          }}
        />
      </div>
    </>
  );
};

export default CreatePOComponent;
